import React, { useState } from "react"
import EditMeterStatusPage from "@/routes/_home/meter-connection-settings/meter-status/edit"
import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import { useForm } from "react-hook-form"
import * as z from "zod"

import { MeterStatusType } from "@/types/meter"
import { deleteMeterStatus } from "@/lib/api/meter"
import { useToast } from "@/hooks/use-toast"
import { Button } from "@/components/ui/button"
import {
  Credenza,
  CredenzaBody,
  CredenzaClose,
  CredenzaContent,
  CredenzaDescription,
  CredenzaFooter,
  CredenzaHeader,
  CredenzaTitle,
} from "@/components/ui/credenza"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { MeterSizeForm } from "@/components/forms/meter-size-form"
import { Header } from "@/components/header"
import { Icons } from "@/components/icons"

interface MeterStatusProps {
  data: MeterStatusType
}

export function MeterStatusOperation({ data }: MeterStatusProps) {
  const [showDropDown, setShowDropDown] = useState<boolean>(false)
  const [showEditModal, setShowEditModal] = useState<boolean>(false)
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)

  return (
    <>
      <DropdownMenu open={showDropDown} onOpenChange={setShowDropDown}>
        <DropdownMenuTrigger>
          <div className="flex h-8 w-8 items-center justify-center rounded-md border transition-colors hover:bg-muted">
            <Icons.ellipsis className="size-4" />
            <span className="sr-only">Open</span>
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onSelect={() => {
              setShowDropDown(false)
              setShowEditModal(!showEditModal)
            }}
          >
            <Icons.edit className="mr-2 size-4" />
            Edit Meter Status
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="text-destructive"
            onSelect={() => {
              setShowDropDown(false)
              setShowDeleteModal(!showDeleteModal) // Open modal
            }}
          >
            <Icons.delete className="mr-2 size-4" />
            Delete Meter Status
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Credenza open={showEditModal} onOpenChange={setShowEditModal}>
        <EditMeterStatusPage
          meterStatusData={data}
          setShowEditMeterStatusModal={setShowEditModal}
        />
      </Credenza>

      <DeleteMeterStatusModal
        meterStatus={data.name}
        meterStatusId={data.meter_status_id}
        showDeleteModal={showDeleteModal}
        setShowDeleteModal={setShowDeleteModal}
      />
    </>
  )
}

function DeleteMeterStatusModal({
  meterStatus,
  meterStatusId,
  showDeleteModal,
  setShowDeleteModal,
}: {
  meterStatus?: string
  meterStatusId?: number
  showDeleteModal: boolean
  setShowDeleteModal: React.Dispatch<React.SetStateAction<boolean>>
}) {
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const formSchema = z.object({
    selected_meter_status: z
      .string()
      .min(1, "Required")
      .refine((val) => val === meterStatus, {
        message: "Selected status must match the status.",
      }),
  })
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      id: meterStatusId,
      selected_meter_status: "",
    },
  })

  const mutation = useMutation({
    mutationFn: async () => {
      if (meterStatusId) {
        return await deleteMeterStatus(meterStatusId)
      }
    },
    onSuccess: () => {
      toast({
        description: "The meter status has been successfully deleted.",
      })
      queryClient.invalidateQueries({ queryKey: ["meterStatusList"] })
      setShowDeleteModal(false)
    },
    onError: (error) => {
      console.error("Delete failed:", error)
      toast({
        variant: "destructive",
        description: "Something went wrong while deleting.",
      })
    },
  })

  function handleDelete() {
    mutation.mutate()
  }

  console.log(showDeleteModal)

  return (
    <Credenza open={showDeleteModal} onOpenChange={setShowDeleteModal}>
      <CredenzaContent>
        <Form {...form}>
          <form
            className="space-y-4 pb-4"
            onSubmit={form.handleSubmit(handleDelete)}
          >
            <CredenzaHeader>
              <CredenzaTitle>Delete Billing User</CredenzaTitle>
              <CredenzaDescription>
                You cannot undo this action!
              </CredenzaDescription>
            </CredenzaHeader>
            <CredenzaBody>
              <FormField
                control={form.control}
                name="selected_meter_status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Type "{meterStatus}" to delete.</FormLabel>
                    <FormControl>
                      <Input {...field} autoComplete="off" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CredenzaBody>
            <CredenzaFooter>
              <CredenzaClose asChild>
                <Button variant={"outline"}>Cancel</Button>
              </CredenzaClose>
              <Button
                disabled={mutation.isPending}
                type="submit"
                variant={"destructive"}
              >
                {mutation.isPending && (
                  <Icons.loader className="mr-2 animate-spin" />
                )}
                Delete
              </Button>
            </CredenzaFooter>
          </form>
        </Form>
      </CredenzaContent>
    </Credenza>
  )
}
